package nl.appstuff.padelchecker.pages.padel

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import nl.appstuff.padelchecker.site.PageSeo
import nl.appstuff.padelchecker.site.StructuredData
import nl.appstuff.padelchecker.site.components.NavBar
import nl.appstuff.padelchecker.site.components.sections.FooterSection
import nl.appstuff.padelchecker.site.directory.Directory
import nl.appstuff.padelchecker.site.directory.DirectoryClub
import nl.appstuff.padelchecker.site.directory.DirectoryContentBox
import nl.appstuff.padelchecker.site.directory.DirectoryHeading
import nl.appstuff.padelchecker.site.directory.DirectoryLink
import nl.appstuff.padelchecker.site.directory.DirectoryParagraph
import nl.appstuff.padelchecker.site.directory.DirectorySubheading
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import org.jetbrains.compose.web.css.vh

@Page("/padel/{city}/{slug}")
@Composable
fun ClubPage() {
    val strings = LocalSiteStrings.current
    val params = rememberPageContext().route.params
    val citySlug = params["city"].orEmpty()
    val clubSlug = params["slug"].orEmpty()
    val club = Directory.club(citySlug, clubSlug)

    if (club == null) {
        PageSeo(
            title = "${strings.directoryClubNotFoundTitle} – Padelchecker",
            description = strings.directoryClubNotFoundBody,
            canonicalPath = "/padel",
        )
    } else {
        val platformsText = if (club.platforms.isEmpty()) "" else
            strings.directoryClubPlatformsSuffix(club.platforms.joinToString(", "))
        PageSeo(
            title = strings.directoryClubSeoTitle(club.name, club.cityName),
            description = strings.directoryClubSeoDescription(club.name, club.cityName, platformsText),
            canonicalPath = "/padel/${club.citySlug}/${club.slug}",
        )
        ClubStructuredData(club)
    }

    Column(modifier = Modifier.fillMaxWidth().minHeight(100.vh)) {
        NavBar()
        DirectoryContentBox {
            if (club == null) {
                DirectoryHeading(strings.directoryClubNotFoundTitle)
                DirectoryParagraph(strings.directoryClubNotFoundBody)
                DirectoryLink("/padel", strings.directoryClubSeeAll)
            } else {
                DirectoryHeading(club.name)
                DirectoryParagraph(
                    strings.directoryClubIntro(
                        club.name,
                        club.cityName,
                        club.provinceName,
                        club.address,
                        if (!club.postalCode.isNullOrBlank()) ", ${club.postalCode}" else "",
                        club.courtCount?.let { strings.directoryClubCourtsSuffix(it) } ?: "",
                        if (club.platforms.isNotEmpty()) strings.directoryClubPlatformsSuffix(club.platforms.joinToString(", ")) else "",
                    )
                )

                DirectorySubheading(strings.directoryClubAvailabilityHeading)
                DirectoryParagraph(strings.directoryClubAvailabilityBody(club.name))
                DirectoryLink("/", strings.directoryClubOpenApp)

                if (!club.websiteUrl.isNullOrBlank()) {
                    DirectorySubheading(strings.directoryClubWebsiteHeading)
                    DirectoryLink(club.websiteUrl, club.websiteUrl)
                }

                DirectoryLink("/padel/${club.citySlug}", strings.directoryClubBackToCity(club.cityName))
                DirectoryLink("/padel-in/${club.provinceSlug}", strings.directoryClubAllInProvince(club.provinceName))
            }
        }
        FooterSection()
    }
}

@Composable
private fun ClubStructuredData(club: DirectoryClub) {
    val sameAs = if (club.websiteUrl.isNullOrBlank()) "" else
        ",\n  \"sameAs\": [${jsonString(club.websiteUrl)}]"
    StructuredData(
        """
        {
          "@context": "https://schema.org",
          "@type": "SportsActivityLocation",
          "name": ${jsonString(club.name)},
          "url": "https://padelchecker.nl/site/padel/${club.citySlug}/${club.slug}",
          "sport": "Padel",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": ${jsonString(club.address)},
            "addressLocality": ${jsonString(club.cityName)},
            "postalCode": ${if (club.postalCode.isNullOrBlank()) "\"\"" else jsonString(club.postalCode)},
            "addressRegion": ${jsonString(club.provinceName)},
            "addressCountry": "NL"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": ${club.latitude},
            "longitude": ${club.longitude}
          }$sameAs
        }
        """.trimIndent()
    )
}
